/**
 * 🎵 Audio Sequence Manager
 * 
 * Gerencia reprodução sequencial automática de áudios baseado na direção da mensagem:
 * - Áudios RECEBIDOS: sequência independente
 * - Áudios ENVIADOS: sequência independente  
 * - TEXTO: para todas as sequências
 * - Mudança de direção: reinicia como manual
 */

class AudioSequenceManager {
  constructor() {
    this.activeDirection = null;    // 'received' | 'sent' | null
    this.currentPlayingId = null;   // ID do áudio tocando atualmente
    this.audioRefs = new Map();     // messageId -> { audioRef, messageIndex, fromMe }
    this.messagesList = [];         // Referência da lista atual de mensagens
  }

  /**
   * Registra um player de áudio no gerenciador
   */
  registerAudio(messageId, audioRef, messageIndex, fromMe) {
    this.audioRefs.set(messageId, {
      audioRef,
      messageIndex,
      fromMe
    });
  }

  /**
   * Remove um player de áudio do gerenciador
   */
  unregisterAudio(messageId) {
    if (this.currentPlayingId === messageId) {
      this.currentPlayingId = null;
      this.activeDirection = null;
    }
    this.audioRefs.delete(messageId);
  }

  /**
   * Atualiza a lista de mensagens
   */
  updateMessagesList(messagesList) {
    this.messagesList = messagesList;
  }

  /**
   * Para todos os áudios tocando
   */
  pauseAll(excludeId = null) {
    this.audioRefs.forEach((audioData, messageId) => {
      if (messageId !== excludeId && audioData.audioRef.current) {
        const audio = audioData.audioRef.current;
        if (!audio.paused) {
          audio.pause();
        }
      }
    });
  }

  /**
   * Inicia reprodução de um áudio (chamado quando usuário clica play)
   */
  startAudio(messageId) {
    const audioData = this.audioRefs.get(messageId);
    if (!audioData) return;

    // Para todos os outros áudios
    this.pauseAll(messageId);

    // Define direção ativa e áudio atual
    this.activeDirection = audioData.fromMe ? 'sent' : 'received';
    this.currentPlayingId = messageId;

  }

  /**
   * Encontra o próximo áudio na sequência
   */
  getNextAudioInSequence(currentMessageId) {
    const currentAudioData = this.audioRefs.get(currentMessageId);
    if (!currentAudioData) return null;

    const currentIndex = currentAudioData.messageIndex;
    const currentDirection = currentAudioData.fromMe ? 'sent' : 'received';

    // Procura próximo áudio na mesma direção
    for (let i = currentIndex + 1; i < this.messagesList.length; i++) {
      const nextMessage = this.messagesList[i];
      
      // Se encontrar texto → para sequência
      if (!nextMessage || nextMessage.mediaType !== 'audio') {
        return null;
      }
      
      // Verifica direção da próxima mensagem
      const nextDirection = nextMessage.fromMe ? 'sent' : 'received';
      
      // Se mudou direção → para sequência  
      if (nextDirection !== currentDirection) {
        return null;
      }
      
      // Se é áudio da mesma direção → continua
      if (nextMessage.mediaType === 'audio') {
        return nextMessage;
      }
    }
    
    return null;
  }

  /**
   * Reproduz automaticamente o próximo áudio quando o atual termina
   */
  onAudioEnded(messageId) {
    if (this.currentPlayingId !== messageId) return;

    const nextMessage = this.getNextAudioInSequence(messageId);
    
    if (nextMessage) {
      // Encontrou próximo áudio na sequência
      const nextAudioData = this.audioRefs.get(nextMessage.id);
      
      if (nextAudioData && nextAudioData.audioRef.current) {
        // Auto-play do próximo áudio
        this.currentPlayingId = nextMessage.id;
        
        const nextAudio = nextAudioData.audioRef.current;
        nextAudio.play().catch(error => {
          console.error('Erro ao reproduzir próximo áudio:', error);
          this.stopSequence();
        });
      } else {
        this.stopSequence();
      }
    } else {
      // Fim da sequência
      this.stopSequence();
    }
  }

  /**
   * Para a sequência atual
   */
  stopSequence() {
    this.currentPlayingId = null;
    this.activeDirection = null;
  }

  /**
   * Verifica se um áudio específico está tocando
   */
  isPlaying(messageId) {
    return this.currentPlayingId === messageId;
  }

  /**
   * Obtém a direção ativa atual
   */
  getActiveDirection() {
    return this.activeDirection;
  }

  /**
   * Retorna dados do áudio atualmente tocando (id, ref, índice, fromMe)
   */
  getCurrentPlaying() {
    if (!this.currentPlayingId) return null;
    const data = this.audioRefs.get(this.currentPlayingId);
    if (!data) return null;
    return {
      messageId: this.currentPlayingId,
      audioRef: data.audioRef,
      messageIndex: data.messageIndex,
      fromMe: data.fromMe,
    };
  }
}

// Instância global singleton
const audioSequenceManager = new AudioSequenceManager();

export default audioSequenceManager;
